// Right-slide drawer that runs `cmd=deep_research` for a (company, role) and
// shows the streaming markdown output. If a prior research file exists on
// disk (saved by interview-backend.mjs into interview-prep/), offer to view
// that instead of re-running.
//
// Used by JobDetail.tsx + ApplicationDetail.tsx. Drives state via
// useJobRunner so the job survives page refresh / drawer re-mount.

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { X, Download, Play, RefreshCw, Trash2 } from 'lucide-react';
import Drawer from '../ui/Drawer';
import Progress from '../ui/Progress';
import { apiDelete, apiGet } from '../../lib/api';
import { useJobRunner } from '../../lib/useJobRunner';
import { cleanLLMOutput } from '../../lib/clean-llm-output';

interface Props {
  open: boolean;
  company: string;
  role?: string;
  onClose: () => void;
}

interface PrepListItem {
  file: string;
  kind: 'research' | 'prep' | 'other';
  company_slug: string | null;
  date: string | null;
  url: string;
  bytes: number;
  mtime: number;
}
interface PrepListResponse { total: number; items: PrepListItem[] }

function companySlug(s: string): string {
  return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function fmtAge(mtime: number): string {
  const h = (Date.now() - mtime) / 3600_000;
  if (h < 1) return `${Math.max(1, Math.floor(h * 60))} min ago`;
  if (h < 48) return `${Math.floor(h)} h ago`;
  return `${Math.floor(h / 24)} d ago`;
}

export default function DeepResearchDrawer({ open, company, role, onClose }: Props) {
  const slug = companySlug(company);
  const companyLower = (company || '').toLowerCase();
  // Reattach: deep_research payload has company. Match by lowercased
  // company so a research run kicked off from another tab/device shows
  // up here in the drawer immediately.
  const runner = useJobRunner(`research-${slug}`, {
    reattach: {
      cmd: 'deep_research',
      match: (p) => !!companyLower && String(p.company || '').toLowerCase() === companyLower,
    },
  });

  // List existing research files for this company
  const listQ = useQuery({
    queryKey: ['interview-prep-list', slug],
    queryFn: () =>
      apiGet<PrepListResponse>('/api/interview-prep/list', { company_slug: slug }),
    enabled: open && !!slug,
    staleTime: 5_000,
    refetchInterval: runner.isRunning ? 4000 : false,
  });

  const latestResearch = useMemo(() => {
    const items = (listQ.data?.items ?? []).filter((i) => i.kind === 'research');
    if (items.length === 0) return null;
    return items.sort((a, b) => b.mtime - a.mtime)[0];
  }, [listQ.data]);

  // When the user asks to view the saved file, fetch its contents
  const [viewingSaved, setViewingSaved] = useState(false);
  const savedQ = useQuery({
    queryKey: ['interview-prep-md', latestResearch?.file],
    queryFn: () =>
      fetch(latestResearch!.url).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      }),
    enabled: viewingSaved && !!latestResearch?.url,
    staleTime: 30_000,
  });

  // Reset "viewing saved" on open so user is presented with the choice each time
  useEffect(() => {
    if (open) setViewingSaved(false);
  }, [open]);

  // Armed delete for the saved research file. 4s window.
  const qc = useQueryClient();
  const [armedDel, setArmedDel] = useState<string | null>(null);
  useEffect(() => {
    if (!armedDel) return;
    const t = setTimeout(() => setArmedDel(null), 4000);
    return () => clearTimeout(t);
  }, [armedDel]);
  const deleteSaved = async (file: string) => {
    if (armedDel !== file) { setArmedDel(file); return; }
    setArmedDel(null);
    try {
      await apiDelete(`/api/interview-prep/${encodeURIComponent(file)}`);
      // Invalidate the prep list so this drawer + PrepPanel both refresh
      qc.invalidateQueries({ queryKey: ['interview-prep-list'] });
      setViewingSaved(false);
    } catch (e) {
      alert('Delete failed: ' + (e as Error).message);
    }
  };

  const runResearch = () => {
    runner.startJob('deep_research', { company, role: role || '' });
    setViewingSaved(false);
  };

  const showRunner = runner.isActive && !viewingSaved;
  const showSaved = viewingSaved || (!runner.isActive && !!latestResearch && !runner.log);

  return (
    <Drawer open={open} onClose={onClose} width={640}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '18px 22px',
          borderBottom: '1px solid var(--line)',
          gap: 10,
        }}
      >
        <div>
          <div className="eyebrow">Deep research</div>
          <div className="serif" style={{ fontSize: 22, lineHeight: 1.2 }}>{company}</div>
          {role && <div style={{ fontSize: 12, color: 'var(--ink3)' }}>{role}</div>}
        </div>
        <button className="btn ghost" onClick={onClose} style={{ padding: '4px 8px' }} aria-label="Close">
          <X size={16} />
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '18px 22px' }}>
        {/* Intro / choice panel when nothing is active */}
        {!showRunner && !showSaved && (
          <div>
            {latestResearch ? (
              <div
                className="card"
                style={{ padding: 16, marginBottom: 16, background: 'rgba(45,122,74,0.04)', border: '1px solid rgba(45,122,74,0.25)' }}
              >
                <div className="tab" style={{ marginBottom: 4 }}>Found saved research</div>
                <div style={{ fontSize: 13, color: 'var(--ink2)', marginBottom: 10 }}>
                  {latestResearch.file} · {fmtAge(latestResearch.mtime)} · {(latestResearch.bytes / 1024).toFixed(1)} KB
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn accent" onClick={() => setViewingSaved(true)}>View saved →</button>
                  <button className="btn" onClick={runResearch} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <RefreshCw size={12} /> Run again
                  </button>
                </div>
              </div>
            ) : (
              <div className="empty" style={{ padding: '32px 12px' }}>
                <span className="serif">No prior research for {company}.</span>
                This runs a background LLM job (~2 min) and saves a markdown report you can revisit later.
              </div>
            )}
            {!latestResearch && (
              <button
                className="btn accent lg"
                onClick={runResearch}
                style={{ width: '100%', display: 'inline-flex', justifyContent: 'center', gap: 8, alignItems: 'center' }}
              >
                <Play size={14} /> Start deep research
              </button>
            )}
          </div>
        )}

        {/* Live job progress */}
        {showRunner && (
          <div>
            <Progress
              value={runner.status === 'done' ? 100 : runner.status === 'failed' ? 100 : Math.min(95, runner.log.length / 50)}
            />
            <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 6, marginBottom: 14 }}>
              Job {runner.jobId?.slice(0, 8)} · {runner.status} · {runner.log.length.toLocaleString()} chars
            </div>
            {runner.errorLine && (
              <div style={{ marginBottom: 14, padding: '10px 12px', background: '#fcf3e6', border: '1px solid #eddbc1', borderRadius: 8, fontSize: 12, color: 'var(--warn)' }}>
                {runner.errorLine}
              </div>
            )}
            <div className="report-md" style={{ fontSize: 13.5, lineHeight: 1.65, color: 'var(--ink)' }}>
              {runner.log ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{cleanLLMOutput(runner.log)}</ReactMarkdown>
              ) : (
                <div style={{ color: 'var(--ink3)' }}>Starting Ollama…</div>
              )}
            </div>
            {runner.status === 'done' && (
              <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                <button className="btn" onClick={runner.dismiss}>Clear log</button>
                <button className="btn" onClick={runResearch} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                  <RefreshCw size={12} /> Run again
                </button>
              </div>
            )}
          </div>
        )}

        {/* Rendering of a saved file */}
        {showSaved && latestResearch && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
              <a
                className="btn"
                href={latestResearch.url}
                download={latestResearch.file}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <Download size={12} /> Download .md
              </a>
              <button
                className="btn"
                onClick={runResearch}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <RefreshCw size={12} /> Run again (overwrite)
              </button>
              {/* Armed delete: clicks DELETE /api/interview-prep/:file. After
                  delete, drawer falls back to "no prior research" state. */}
              <button
                className="btn ghost"
                onClick={() => deleteSaved(latestResearch.file)}
                title={
                  armedDel === latestResearch.file
                    ? 'Click again to permanently delete this saved research'
                    : 'Delete this saved research file from disk'
                }
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  color: armedDel === latestResearch.file ? '#fff' : '#b43a3a',
                  background: armedDel === latestResearch.file ? '#b43a3a' : 'transparent',
                  border: armedDel === latestResearch.file ? '1px solid #b43a3a' : undefined,
                }}
              >
                {armedDel === latestResearch.file ? <><X size={12} /> Sure?</> : <><Trash2 size={12} /> Delete</>}
              </button>
              <div style={{ flex: 1 }} />
              <div style={{ fontSize: 11, color: 'var(--ink3)' }}>
                {latestResearch.file}
              </div>
            </div>
            <div className="report-md" style={{ fontSize: 13.5, lineHeight: 1.65 }}>
              {savedQ.isLoading && <div style={{ color: 'var(--ink3)' }}>Loading…</div>}
              {savedQ.data && <ReactMarkdown remarkPlugins={[remarkGfm]}>{savedQ.data}</ReactMarkdown>}
              {savedQ.isError && <div style={{ color: 'var(--warn)' }}>Couldn't load saved research.</div>}
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
}

